/**
 * CompactResizeHandles — the three drag affordances that make the Compact VQE
 * popover resizable: a right edge (width), a bottom edge (height), and a
 * bottom-right corner (both) carrying a visible diagonal grip indicator.
 *
 * Rendered as absolutely-positioned siblings of the popover's scroll region so
 * they pin to the popover's edges and never scroll with the content. The actual
 * size state + pointer math lives in `useResizableBox`; this is pure markup +
 * the per-handle pointer-down wiring.
 */

import type { ResizeAxis } from './useResizableBox';

export interface CompactResizeHandlesProps {
  readonly startResize: (axis: ResizeAxis) => (e: React.PointerEvent) => void;
}

export function CompactResizeHandles({ startResize }: CompactResizeHandlesProps) {
  return (
    <>
      {/* Right edge — horizontal resize. */}
      <div
        onPointerDown={startResize('x')}
        className="absolute right-0 top-0 bottom-3 z-20 w-1.5 cursor-ew-resize hover:bg-primary/20"
        data-testid="vqe-compact-resize-right"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize width"
      />
      {/* Bottom edge — vertical resize. */}
      <div
        onPointerDown={startResize('y')}
        className="absolute bottom-0 left-0 right-3 z-20 h-1.5 cursor-ns-resize hover:bg-primary/20"
        data-testid="vqe-compact-resize-bottom"
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize height"
      />
      {/* Bottom-right corner — both axes, with a visible grip. Rendered last so
          it sits above the two edges where they meet. */}
      <div
        onPointerDown={startResize('xy')}
        className="absolute bottom-0 right-0 z-30 flex h-3.5 w-3.5 cursor-nwse-resize items-end justify-end text-muted-foreground/70 hover:text-foreground"
        data-testid="vqe-compact-resize-corner"
        role="separator"
        aria-label="Resize"
      >
        <svg
          viewBox="0 0 10 10"
          className="h-2.5 w-2.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="round"
          aria-hidden
        >
          <path d="M9 2 L2 9 M9 6 L6 9" />
        </svg>
      </div>
    </>
  );
}
